import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowUpRight, Mail, Phone, MapPin, Globe, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

export const Footer = () => {
  const { t } = useLanguage();

  const quickLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/products', label: t('nav.products') },
    { path: '/markets', label: t('nav.markets') },
    { path: '/about', label: t('nav.about') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const categories = [
    { path: '/products?category=rice', label: t('footer.cat_rice') },
    { path: '/products?category=steel', label: t('footer.cat_steel') },
    { path: '/products?category=generators', label: t('footer.cat_generators') },
    { path: '/products?category=batteries', label: t('footer.cat_batteries') },
    { path: '/products?category=tiles', label: t('footer.cat_tiles') },
  ];

  return (
    <footer className="relative bg-gradient-to-b from-[#0a1628] to-[#060d17] text-white overflow-hidden" data-testid="footer">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, rgba(197, 160, 89, 0.1) 0%, transparent 50%),
                           radial-gradient(circle at 80% 20%, rgba(197, 160, 89, 0.08) 0%, transparent 40%)`
        }} />
      </div>
      
      {/* Main Footer */}
      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-4 space-y-6">
            <Link to="/" className="inline-block">
              <img 
                src="/varyaz-logo-full.png" 
                alt="Varyaz Trade" 
                className="h-14 w-auto object-contain"
              />
            </Link>
            <p className="text-white/50 leading-relaxed max-w-sm text-sm">
              {t('footer.description')}
            </p>
            
            {/* Newsletter */}
            <div className="pt-4">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-secondary-500 mb-4">
                {t('footer.newsletter')}
              </p>
              <form className="flex group" onSubmit={(e) => e.preventDefault()}>
                <Input
                  type="email"
                  placeholder={t('footer.newsletter_placeholder')}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-none flex-1 h-12 focus:border-secondary-500 transition-all"
                  data-testid="newsletter-input"
                />
                <Button 
                  type="submit"
                  className="bg-secondary-500 hover:bg-secondary-400 text-primary-900 rounded-none px-4 h-12"
                  data-testid="newsletter-submit"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2 lg:col-start-6">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-secondary-500 mb-6">
              {t('footer.quick_links')}
            </p>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/60 hover:text-secondary-500 text-sm transition-colors inline-flex items-center gap-2 group"
                    data-testid={`footer-link-${link.path.replace('/', '') || 'home'}`}
                  >
                    <span className="w-0 h-px bg-secondary-500 group-hover:w-3 transition-all" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div className="lg:col-span-2">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-secondary-500 mb-6">
              {t('footer.categories')}
            </p>
            <ul className="space-y-3">
              {categories.map((cat) => (
                <li key={cat.path}>
                  <Link
                    to={cat.path}
                    className="text-white/60 hover:text-secondary-500 text-sm transition-colors inline-flex items-center gap-2 group"
                  >
                    <span className="w-0 h-px bg-secondary-500 group-hover:w-3 transition-all" />
                    {cat.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-secondary-500 mb-6">
              {t('footer.contact')}
            </p>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3 text-white/60 group">
                <div className="w-8 h-8 bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-secondary-500/20 transition-colors">
                  <MapPin className="w-4 h-4 text-secondary-500" />
                </div>
                <span className="pt-1">{t('footer.address')}</span>
              </li>
              <li className="flex items-center gap-3 group">
                <div className="w-8 h-8 bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-secondary-500/20 transition-colors">
                  <Phone className="w-4 h-4 text-secondary-500" />
                </div>
                <a href="https://wa.me/905377020927" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-secondary-500 transition-colors">
                  +90 537 702 09 27
                </a>
              </li>
              <li className="flex items-center gap-3 group">
                <div className="w-8 h-8 bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-secondary-500/20 transition-colors">
                  <Mail className="w-4 h-4 text-secondary-500" />
                </div>
                <a href="mailto:info@varyaztrade.com" className="text-white/60 hover:text-secondary-500 transition-colors">
                  info@varyaztrade.com
                </a>
              </li>
              <li className="flex items-center gap-3 group">
                <div className="w-8 h-8 bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-secondary-500/20 transition-colors">
                  <Globe className="w-4 h-4 text-secondary-500" />
                </div>
                <span className="text-white/60">varyaztrade.com</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/30 text-sm">
              © {new Date().getFullYear()} Varyaz Trade. {t('footer.rights')}
            </p>
            <div className="flex items-center gap-6 text-sm text-white/30">
              <Link to="/privacy" className="hover:text-secondary-500 transition-colors">{t('footer.privacy')}</Link>
              <Link to="/terms" className="hover:text-secondary-500 transition-colors">{t('footer.terms')}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
